import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainSection } from '@/components/layout/MainSection';
import { FilterChip } from '@/components/ui/FilterChip';
import { InlineMessage } from '@/components/ui/InlineMessage';
import { Pagination } from '@/components/ui/Pagination';
import { SearchField } from '@/components/ui/SearchField';
import { UserListItem } from '@/components/AdminUsers/UserListItem';
import { UsersTable } from '@/components/AdminUsers/UsersTable';
import { UsersStatsGrid } from '@/components/AdminUsers/UsersStatsGrid';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { useDebounce } from '@/hooks/useDebounce';
import { ADMIN_USERS_FILTERS } from '@/constants';
import type { AdminUsersFilter } from '@/constants';
import type { AdminUserActionHandlers } from '@/types/adminUsers';
import styles from './UsersTab.module.scss';

const SEARCH_DEBOUNCE_MS = 300;

const FILTER_LABEL_KEYS: Record<AdminUsersFilter, string> = {
  [ADMIN_USERS_FILTERS.ALL]: 'admin:filterAll',
  [ADMIN_USERS_FILTERS.UNVERIFIED]: 'admin:usersFilterUnverified',
  [ADMIN_USERS_FILTERS.PENDING]: 'admin:usersFilterPending',
  [ADMIN_USERS_FILTERS.BLOCKED]: 'admin:usersFilterBlocked',
};

const FILTERS = Object.values(ADMIN_USERS_FILTERS);

const UsersTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const isMobile = useBreakpointDown('lg');

  const [userFilter, setUserFilter] = useState<AdminUsersFilter>(ADMIN_USERS_FILTERS.ALL);
  const [usersPage, setUsersPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  // A new search term starts from the first page.
  const [appliedSearch, setAppliedSearch] = useState(debouncedSearch);
  if (appliedSearch !== debouncedSearch) {
    setAppliedSearch(debouncedSearch);
    setUsersPage(1);
  }

  const {
    usersPage: data,
    isFirstLoad,
    isStaleData,
    stats,
    loadError,
    message,
    dismissMessage,
    pendingActions,
    blockUser,
    unblockUser,
    sendVerification,
  } = useAdminUsers({ filter: userFilter, page: usersPage, search: debouncedSearch });

  const totalPages = data?.pagination.totalPages ?? 1;

  // The current page can outgrow the list (e.g. unblocking the only user on the
  // last page of the Blocked filter): fall back to the last existing page.
  if (data && !isStaleData && totalPages > 0 && usersPage > totalPages) {
    setUsersPage(totalPages);
  }

  if (isFirstLoad) return null;

  const users = data?.users ?? [];

  const handleFilterChange = (filter: AdminUsersFilter) => {
    setUserFilter(filter);
    setUsersPage(1);
  };

  const actionHandlers: AdminUserActionHandlers = {
    pendingActions,
    onSendVerification: sendVerification,
    onBlock: blockUser,
    onUnblock: unblockUser,
  };

  const chipCounts: Record<AdminUsersFilter, number | undefined> = {
    [ADMIN_USERS_FILTERS.ALL]: stats?.users.total,
    [ADMIN_USERS_FILTERS.UNVERIFIED]: undefined,
    [ADMIN_USERS_FILTERS.PENDING]: stats?.users.pending,
    [ADMIN_USERS_FILTERS.BLOCKED]: stats?.users.blocked,
  };

  // Load error first: an older action message must never hide it.
  const banners = (
    <>
      {loadError && <InlineMessage type="error" text={loadError} />}
      {message && (
        <InlineMessage type={message.type} text={message.text} onDismiss={dismissMessage} />
      )}
    </>
  );

  const filterChips = (
    <div className={styles.chips}>
      {FILTERS.map((filter) => (
        <FilterChip
          key={filter}
          label={t(FILTER_LABEL_KEYS[filter])}
          count={chipCounts[filter]}
          pressed={userFilter === filter}
          onClick={() => handleFilterChange(filter)}
          testId={`admin-users-filter-${filter}`}
        />
      ))}
    </div>
  );

  const searchField = (
    <SearchField
      value={search}
      onChange={setSearch}
      placeholder={t('admin:usersSearchPlaceholder')}
      testId="admin-users-search"
    />
  );

  const emptyState = (
    <div className={styles.emptyState} data-testid="admin-users-empty">
      {t('admin:noUsersFound')}
    </div>
  );

  const pagination = (
    <Pagination
      page={usersPage}
      totalPages={totalPages}
      onChange={setUsersPage}
      compact={isMobile}
      testIdPrefix="admin-users"
    />
  );

  if (isMobile) {
    return (
      <MainSection title={t('admin:users')}>
        <div className={styles.mobileWrapper}>
          {banners}

          {stats && (
            <p className={styles.summary}>
              {t('admin:usersSummary', {
                total: stats.users.total,
                verified: stats.users.verified,
                newThisWeek: stats.users.new_this_week,
              })}
            </p>
          )}

          {searchField}

          {filterChips}

          {users.length === 0 ? (
            emptyState
          ) : (
            <div className={styles.list}>
              {users.map((u) => (
                <UserListItem key={u.id} user={u} {...actionHandlers} />
              ))}
            </div>
          )}

          {pagination}
        </div>
      </MainSection>
    );
  }

  return (
    <MainSection title={t('admin:users')}>
      <div className={styles.desktopWrapper}>
        {banners}

        {stats && <UsersStatsGrid stats={stats.users} />}

        <div className={styles.desktopSearch}>{searchField}</div>

        {filterChips}

        {users.length === 0 ? emptyState : <UsersTable users={users} {...actionHandlers} />}

        {pagination}
      </div>
    </MainSection>
  );
};

export default UsersTab;
